import { Box, Button, FormControl, Grid, MenuItem, Select, SelectChangeEvent, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { TransferDetail } from '../../../app/services/api/generated';
import { theme } from '../../../theme';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { useTranslation } from 'react-i18next';
import { TransferItemRow } from '../interfaces/StockTransferDetailsTypes';
import { Copy01 } from '../../../assets';
import { DynamicUomSelect } from '../../../components/shared/DymanicUomSelect';
import Tooltip from '@mui/material/Tooltip';
import UpdateIcon from '@mui/icons-material/Update';

const StockTransferDetailTable = (props: {
  transferDetails: TransferDetail[] | undefined,
  isDeleted: boolean,
  acceptedDate: string | undefined,
  transferType: string,
  transferItemRows: TransferItemRow[],
  handleUomChange: (id: number) => (event: SelectChangeEvent) => void,
  handleQuantityChange: (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => void,
  handleCopyQtysButtonClick: () => void,
  currencyCode?: string
}) => {

  const { t } = useTranslation('stockTransfersDetails');

  const BodyTableCell = (props: { children?: React.ReactNode, align?: 'center' | 'left' }) => {
    return (<TableCell
      scope="row"
      sx={{
        borderBottom: '1px solid',
        borderBottomColor: theme.palette.custom.gray[200],
        textAlign: props.align ?? 'left',
      }}
    >
      {props.children}
    </TableCell>);
  };

  return (
    <Box sx={{ width: '100%', mt: '30px' }}>
      <Grid container
        sx={{ mb: '8px' }}>
        <Grid item
          xs={8}></Grid>
        <Grid item
          xs={4}
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: '8px' }}>
          {!props.isDeleted && !props.acceptedDate && props.transferType === 'Incoming' && <Button
            variant="primary"
            size='lg'
            startIcon={<SvgIcon><Copy01 color='white' /></SvgIcon>}
            onClick={props.handleCopyQtysButtonClick}
          >{t('copyQuantitiesButton')}
          </Button>}
        </Grid>
      </Grid>
      <TableContainer component={'div'}>
        <Table
          size="small"
          aria-label="a table"
          sx={{
            borderBottom: '1px solid',
            borderTop: '1px solid',
            borderColor: theme.palette.custom.gray[200],
            width: '100%',
          }}
        >
          <TableHead sx={{ paddingTop: '10px', backgroundColor: theme.palette.custom.gray[200] }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}
                align="left">
                {t('table.itemNumber')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}
                align="left">
                {t('table.itemDescription')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}
                align="left">
                {t('table.requestedQuantity')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}
                align="left">
                {t('table.receivedQuantity')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}
                align="left">
                {t('table.requestedValue')}
              </TableCell>
              {props.acceptedDate && <TableCell sx={{ fontWeight: 'bold' }}
                align="left">
                {t('table.receivedValue')}
              </TableCell>}

            </TableRow>
          </TableHead>
          <TableBody>
            {props.transferDetails?.map((row, index) => (
              <TableRow
                key={row.id?.toString() + '-' + row.itemNumber}
                sx={{ backgroundColor: theme.palette.custom.gray[50], '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <BodyTableCell>{row.itemNumber}</BodyTableCell>
                <BodyTableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {row.newItemNumber && (
                      <Tooltip
                        title={t('inTransition', { newItemNumber: row.newItemNumber })}
                        slotProps={{ tooltip: { sx: { maxWidth: 'none', whiteSpace: 'nowrap' } } }}
                      >
                        <UpdateIcon sx={{ fontSize: 18, mr: 1 , mb: 2 }} />
                      </Tooltip>
                    )}
                    {row.description}
                  </Box>
                </BodyTableCell>
                <BodyTableCell>{row.primaryUom}</BodyTableCell>
                <BodyTableCell>{row.requestedQuantity}</BodyTableCell>
                { !props.isDeleted && !props.acceptedDate && props.transferType === 'Incoming' && <BodyTableCell>
                  <FormControl fullWidth
                    size='small'>
                    <Select
                      labelId={`uoms-label-${row.id}`}
                      id={`uoms-select-${row.id}`} 
                      value={props.transferItemRows.find(x => x.id === row.id)?.selectedItemUom ?? ''}
                      sx={{ backgroundColor: theme.palette.common.white }}
                      disabled={row.isDepositFeeItem == true}
                      onChange={props.handleUomChange(row.id ?? -1)}
                    >
                      <MenuItem value=''>
                        <em>{t('table.none')}</em>
                      </MenuItem>
                      {props.transferItemRows.find(x => x.id === row.id)?.possibleItemUoms?.map((uom) => {
                        return <MenuItem key={uom.code}
                          value={uom.code}>{uom.code}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                </BodyTableCell>}
                <BodyTableCell>
                  {!props.isDeleted && !props.acceptedDate && props.transferType === 'Incoming' &&
                    (() => {
                      const currentRow = props.transferItemRows.find(x => x.id === row.id);
                      const selectedItemUom = currentRow?.selectedItemUom;
                      const receivedQuantity = currentRow?.receivedQuantity;

                      const fallbackQuantity = (!row.isAllowPartialUomTransactions || row.primaryUom !== selectedItemUom)
                        ? 0
                        : '0.00';

                      const quantity = receivedQuantity ?? fallbackQuantity;

                      return (
                        <DynamicUomSelect
                          disabled={row.isDepositFeeItem}
                          formatDecimalOnLoad={false}
                          allowPrimaryUomDecimal={row.isAllowPartialUomTransactions}
                          id={`DynamicUomSelect-${row.id}`}
                          seletedUom={selectedItemUom ?? ''}
                          onUomChange={props.handleUomChange(row.id ?? -1)}
                          uoms={currentRow?.possibleItemUoms ?? []}
                          quantity={quantity}
                          onQuantityChange={props.handleQuantityChange(row.id ?? -1)}
                        />
                      );
                    })()}
                  {(props.isDeleted || props.acceptedDate || props.transferType === 'Outgoing') && ((row.receivedQuantity !== null) ?
                    <DynamicUomSelect
                      disabled
                      seletedUom={row.primaryUom ?? ''}
                      uoms={[{ code: row.primaryUom ?? '' }]}
                      quantity={row.receivedQuantity} />
                    :
                    '-')}
                </BodyTableCell>
                <BodyTableCell>
                  <RoundAndFormatValue value={row.requestedValue!}
                    currencyCode={props.currencyCode} />
                </BodyTableCell>
                {props.acceptedDate && <BodyTableCell>{row.receivedValue !== undefined ? <RoundAndFormatValue value={row.receivedValue}
                  currencyCode={props.currencyCode} /> : '-'}</BodyTableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StockTransferDetailTable;
